import { ApiError, type Transport } from '@lib/api';
import { errorCodeMessageKey } from '@lib/errorCodes';

/**
 * `transport` → i18n key, total over `Transport` (#2316). `Record<Transport,
 * string>` rather than an optional index: adding a member to
 * `TRANSPORT_BY_AXIOS_CODE` in `api.ts` without adding a row here is then a
 * TYPE ERROR, not a silent fallthrough to the fallback key.
 */
const TRANSPORT_MESSAGE_KEY: Record<Transport, string> = {
  timeout: 'error.timeout',
  network: 'error.network',
};

export interface ResolveApiErrorOpts {
  /**
   * i18n namespace the feature owns (e.g. `'users'`, `'dispatch'`). When
   * provided, backend sentinels from `body.extras.error` are mapped to
   * `${namespace}.error.${sentinel}` and resolved via i18n. Omit when
   * the surface has no feature-local error catalog — the helper then
   * falls straight to `fallbackKey` for any error.
   */
  namespace?: string;
  /**
   * i18n key of the human-readable label shown to the user when no
   * feature-specific sentinel matches. Passed as a KEY, not a
   * pre-translated string — the helper owns `i18n.t` calls so callers
   * do not need to sprinkle `t()` / `i18n.t()` ceremony.
   */
  fallbackKey: string;
}

/**
 * Resolve a backend API error to a localized string the UI can render.
 *
 * Contract: `respondWithError` (backend/src/utils/response.ts) may emit a
 * string sentinel at `body.extras.error`. Each feature owns an i18n key
 * namespace (`users.error.*`, `dispatch.error.*`, …) that mirrors the
 * sentinels the backend emits. The helper:
 *
 *   1. Extract the sentinel (if any) from the `ApiError.body`.
 *   2. If `namespace` is given and `${namespace}.error.${sentinel}` exists
 *      in the current locale, return the translated string.
 *   3. If the closed-set `code` maps to an existing key, return that.
 *   4. If the failure never reached a verdict at the transport layer,
 *      return that transport's own key via `TRANSPORT_MESSAGE_KEY` —
 *      `error.timeout` (#2289) or `error.network` (#2316).
 *   5. Otherwise, return the translated `fallbackKey`.
 *
 * Raw `err.message` is never surfaced to the UI — it can leak backend
 * internals (SQL fragments, stack traces in unhandled paths). Pair this
 * helper with `console.error(err)` at the catch site if dev debugging
 * needs the raw message.
 *
 * Silent drift detection: when a sentinel exists but no matching key is
 * defined, a dev-only `console.warn` fires so the coverage gap shows up
 * in the dev console instead of degrading UX without a trace.
 *
 * Assumes i18next is initialized with `keySeparator: false` — see
 * `frontend/src/i18n/index.ts`. Flipping keySeparator back on would make
 * `i18n.exists('users.error.foo')` do nested-object lookup and every
 * sentinel would silently fall through to the fallback branch.
 */
/**
 * The two i18n capabilities this helper actually uses. Narrowed from the whole
 * i18next instance in #2165 so the resource kit — which holds a `t` and a
 * key-existence probe, and deliberately no i18next — can call it too. Every
 * existing caller passes the live instance, which satisfies this structurally.
 */
export interface ErrorI18n {
  t(key: string): string;
  exists(key: string): boolean;
}

export function resolveApiError(err: unknown, i18n: ErrorI18n, opts: ResolveApiErrorOpts): string {
  const { namespace, fallbackKey } = opts;

  if (err instanceof ApiError) {
    // 1. Feature-local sentinel (most specific). `respondWithError` may emit
    //    a string sentinel at `error.extras.error`, surfaced on the thrown
    //    `ApiError.extras`. When a matching `${namespace}.error.<sentinel>`
    //    key exists, it wins.
    const sentinel = (err.extras as { error?: unknown } | undefined)?.error;
    if (namespace && typeof sentinel === 'string' && sentinel.length > 0) {
      const key = `${namespace}.error.${sentinel}`;
      if (i18n.exists(key)) return i18n.t(key);
      if (import.meta.env.DEV) {
        console.warn(
          `[${namespace}] unmapped backend error sentinel — add a locale key to close the gap`,
          {
            sentinel,
            expectedKey: key,
          },
        );
      }
    }

    // 2. Closed-set error code (ADR-0006 §5). The generic, always-available
    //    channel: `error.code.<CODE>` → localized string. Keyed off the
    //    machine `code`, never the human `message`.
    if (err.code) {
      const codeKey = errorCodeMessageKey(err.code);
      if (i18n.exists(codeKey)) return i18n.t(codeKey);
    }

    // 4. Client-side transport failure — timeout (#2289) or network
    //    (#2316). Reachable only because these produce no response, so
    //    nothing above can fire. Last by default, not by rule — `transport`
    //    and `code` cannot coexist today (one comes off a response this
    //    error never had), so moving this line above branch 1 leaves every
    //    test green. It sits here so a server's own answer keeps winning if
    //    that ever stops being true. The keys are literals shipped in every
    //    locale, hence no `exists` guard.
    if (err.transport) return i18n.t(TRANSPORT_MESSAGE_KEY[err.transport]);
  }

  // 5. Caller-provided fallback.
  return i18n.t(fallbackKey);
}
